import { getAutoOrderTypeByFrequenceId } from "@/app/ec/product/callToAction";
import { CheckoutResponse } from "@/fetch/getCheckoutResponse";
import { useGetShoppingCartQuery } from "@/store/shoppingCartApi";
import { UserState, useGetTotalOrdersQuery, useGetUserQuery } from "@/store/userApi";
import { convertShoppingCartProductToAnalyticsReportProductV2 } from "@/utils/analytics/convertToAnalyticsReportProductV2";
import {
  AdditionalAnalyticsProductInfo,
  AnalyticsReportProductV2,
  IncomingAnalyticsProduct,
  convertUserStateToUserDataForAnalytics,
  dispatchAnalyticsEvent,
  dispatchNullEcommerceEvent,
  modifyCartAnalyticsEvent,
} from "@/utils/analyticsEventSender";
import moment from "moment";

export type PageTypes = "n/a" | "product-detail";

export default function useAnalyticsManager() {
  // window.dataLayer = (window.dataLayer || []);
  const { data: user } = useGetUserQuery();
  const { data: shoppingCart } = useGetShoppingCartQuery();
  // /total-orders is auth-required — skip for guests (and while user is still loading)
  // to avoid 401s in the analytics path.
  const { data: totalOrders } = useGetTotalOrdersQuery(undefined, {
    skip: !user || user.IsGuestUser === true,
  });

  const dispatchAnalyticsEventWithShoppintCartAndUserData = (eventName: string) => {
    dispatchAnalyticsEvent({
      eventName: eventName,
      shoppingCartSummary: getShoppingCartSummary(),
      userData: user?.IsGuestUser === false ? { userId: user.UserId } : undefined,
    });
  };

  const getShoppingCartSummary = (): AnalyticsCartSummary => {
    if (!shoppingCart) {
      return {
        totalQuantity: 0,
        subTotal: 0,
        totalShipping: 0,
        products: [],
      };
    }
    const vendorOrderProducts: AnalyticsSummaryCartProduct[] = shoppingCart.vendorOrders.flatMap((vendorOrder) => {
      return vendorOrder.products.map((product) => {
        return {
          productId: product.mpId,
          name: product.productTitle,
          quantity: product.quantity,
          price: product.unitPrice,
          subTotal: product.subtotal,
          category: product.categoryName,
        };
      });
    });
    const totalQuantity: number =
      vendorOrderProducts.length > 0
        ? vendorOrderProducts.map((vp) => vp.quantity).reduce((previousValue, quantity) => previousValue + quantity, 0)
        : 0;
    return {
      totalQuantity: totalQuantity,
      subTotal: shoppingCart.subTotal,
      totalShipping: shoppingCart.standardShipping,
      products: vendorOrderProducts,
    };
  };
  function convertImpressionProductForReport(products: Array<ImpressionProduct>, source: string): Impression[] {
    const impressions: Impression[] = [];
    for (const product of products) {
      if (!product) continue;
      const impression: Impression = {
        id: product.MpId,
        name: product.MpName,
        list: source,
        // list_position: // TODO
      };

      if (product.BrandName) impression["brand"] = product.BrandName;
      // if (product.CategoryName)
      // impression['category'] = product.CategoryName;
      if (product.MinQty) impression["quantity"] = product.MinQty;
      if (product.BestPrice) impression["price"] = product.BestPrice;
      if (product.schema) impression["schema"] = product.schema;
      impressions.push(impression);
    }
    return impressions;
  }
  return {
    dispatchAnalyticsEvent: dispatchAnalyticsEvent,
    dispatchProductImpression: (
      products: Array<ImpressionProduct>,
      source: string,
      user?: UserState,
      certonaPageId?: string,
    ) => {
      const impressions: Impression[] = convertImpressionProductForReport(products, source);
      const event: DispatchAnalyticsEventProps = {
        eventName: "impressionPush",
        ecommerce: {
          impressions: impressions,
        },
      };
      if (certonaPageId) event.certonaPageId = certonaPageId;
      if (user && user.IsGuestUser === false) {
        event.userData = {
          userName: user.UserName,
          userId: user.UserId,
          userEmailAddress: user.EmailAddress,
          firstName: user.FirstName,
          lastName: user.LastName,
          isDentalCustomer: user.IsProfessionalOffice != undefined ? user.IsProfessionalOffice : false,
        };
      }
      dispatchAnalyticsEvent(event);
    },
    dispatchAddToAutoOrderEvent: (mpId: number, frequencyId: string, quantity: number, source: string) => {
      const frequencyInfo = getAutoOrderTypeByFrequenceId(frequencyId);
      const frequencyText = "Every " + frequencyInfo.description;
      dispatchAnalyticsEvent({
        eventName: "autoOrderAdd",
        mpId: mpId,
        frequency: frequencyText,
        quantity: quantity,
        source: source,
      });
    },
    dispatchAggregateProductImpressions: (
      productGroups: { [key: string]: ImpressionProduct[] },
      source: string,
      pageType: PageTypes,
      context?: string,
      certonaPageId?: string,
    ) => {
      let impressions: Impression[] = [];
      for (const key in productGroups) {
        const convertedImpressions = convertImpressionProductForReport(productGroups[key], source);
        impressions = impressions.concat(convertedImpressions);
      }
      const event: DispatchAnalyticsEventProps = {
        eventName: "impressionPush",
        ecommerce: {
          impressions: impressions,
        },
        context: context,
        pageType: pageType,
        certonaPageId: certonaPageId,
      };
      if (user && user.IsGuestUser === false) {
        event.userData = {
          userName: user.UserName,
          userId: user.UserId,
          userEmailAddress: user.EmailAddress,
          firstName: user.FirstName,
          lastName: user.LastName,
          isDentalCustomer: user.IsProfessionalOffice != undefined ? user.IsProfessionalOffice : false,
        };
      }
      dispatchAnalyticsEvent(event);
    },
    /**
     * Dispatches an add or removed from cart event for the products passed to it.
     * It determines which is which by the quantity.
     * @param products
     * @param source
     */
    dispatchModifyCartEvent(products: Array<IncomingAnalyticsProduct>, source: AnalyticsSource, queryId?: string) {
      modifyCartAnalyticsEvent(products, user, source, false, queryId, shoppingCart, totalOrders);
    },
    dispatchOldRegistrationEvent(
      location: Location,
      isCheckout: boolean,
      user: WindfallRestfulResponse.ResponsePieces.CurrentUser,
      subscribed: boolean,
      businessInfo?: { id: number; value: string },
      customDescription?: string,
      customConsumerSpeciality?: number,
    ) {
      const { registrationSource, redirectURI } = (() => {
        if (isCheckout) {
          return {
            registrationSource: "Registered via Checkout Login page",
            redirectURI: "/checkout",
          };
        } else {
          let origin = new URLSearchParams(window.location.search).get("origin");
          origin = origin ? origin : "/";
          return {
            registrationSource: "Registered via Login page",
            redirectURI: origin,
          };
        }
      })();
      const getCustomDescription = () => {
        if (customConsumerSpeciality === 3100) {
          return "Im shopping for personal needs";
        }
        if (customConsumerSpeciality === 3200) {
          return customDescription;
        }
        return "";
      };
      dispatchAnalyticsEvent({
        eventName: "registrationSuccess",
        registrationSource: registrationSource,
        registrationData: {
          oldRegistrationEvent: true,
          userName: user.UserName,
          userId: user.UserId,
          emailAddress: user.EmailAddress,
          subscribed: subscribed ? "true" : "false",
          isProfessionalOffice: user.IsProfessionalOffice === true ? "true" : "false",
          isDentist: businessInfo && businessInfo.id == 100 ? "true" : "false",
          isBusiness: businessInfo && businessInfo.value ? "true" : "false",
          businessType: businessInfo && businessInfo.value ? businessInfo.value : "Not a business",
          registrationSource: isCheckout === true ? "checkout" : "registration page",
          firstName: user.FirstName,
          lastName: user.LastName,
          userHash: user.UserIdHash,
          whatBestDescribesYou: getCustomDescription(),
        },
        eventCallback: () => {
          window.location.href = redirectURI;
        },
        eventTimeout: 2000,
      });
    },
    dispatchRegistrationEvent(
      {
        isCheckout,
        userId,
        userIdHash,
        emailAddress,
        firstName,
        lastName,
        isDentalOffice,
        subscribed,
        onBehalfOfBusiness,
        businessType,
        officeType,
        whatBestDescribesYou,
        officeSpecialty,
        officeName,
        officeContactFirstName,
        officeContactLastName,
        officeContactJobTitle,
        dentalPracticeStructure,
        dentalPracticeNumberOfLocations,
        howYouHeardAboutUs,
      }: DispatchRegistrationEventProps,
      eventCallback?: () => void,
    ) {
      dispatchAnalyticsEvent({
        eventName: "registrationSuccess",
        registrationSource: isCheckout ? "Registered via Checkout Login page" : "Registered via Login page",
        registrationData: {
          userId: userId,
          emailAddress: emailAddress,
          firstName: firstName,
          lastName: lastName,
          subscribed: subscribed ? "true" : "false",
          isProfessionalOffice: isDentalOffice ? "true" : "false",
          isDentist: isDentalOffice ? "true" : "false",
          isBusiness: onBehalfOfBusiness ? "true" : "false",
          businessType: businessType,
          registrationSource: isCheckout === true ? "checkout" : "registration page",
          userHash: userIdHash,
          officeSpecialty: officeSpecialty,
          officeType: officeType,
          officeName: officeName,
          officeContactFirstName: officeContactFirstName,
          officeContactLastName: officeContactLastName,
          officeContactJobTitle: officeContactJobTitle,
          dentalPracticeStructure: dentalPracticeStructure,
          dentalPracticeNumberOfLocations: dentalPracticeNumberOfLocations,
          howYouHeardAboutUs: howYouHeardAboutUs,
          whatBestDescribesYou: whatBestDescribesYou,
          // userName: emailAddress,
        },
        eventCallback: eventCallback,
        eventTimeout: eventCallback ? 2000 : undefined,
      });
    },

    getShoppingCartSummary: getShoppingCartSummary,

    dispatchShoppingCartViewEvent() {
      dispatchAnalyticsEventWithShoppintCartAndUserData("shoppingCartView");
    },

    dispatchAutoOrderViewEvent() {
      dispatchAnalyticsEventWithShoppintCartAndUserData("autoOrderView");
    },

    dispatchUserHasLoggedInEvent(analyticsUserData: AnalyticsUserData) {
      dispatchAnalyticsEvent({
        eventName: "userHasLoggedIn",
        userData: {
          userId: analyticsUserData.userId,
          userName: analyticsUserData.userName,
          userEmailAddress: analyticsUserData.userEmailAddress,
          firstName: analyticsUserData.firstName,
          lastName: analyticsUserData.lastName,
          isDentalCustomer:
            analyticsUserData.isDentalCustomer != undefined ? analyticsUserData.isDentalCustomer : false,
        },
      });
    },

    dispatchProductDetailPageView(
      productViewed: AnalyticsReportProductV2,
      additionalProductInfo: AdditionalAnalyticsProductInfo,
      bestPrice: number,
    ) {
      const userData: AnalyticsUserData | undefined = convertUserStateToUserDataForAnalytics(user, totalOrders);
      dispatchNullEcommerceEvent();
      dispatchAnalyticsEvent({
        eventName: "view_item",
        ecommerce: {
          currency: "USD",
          value: bestPrice,
          items: [productViewed],
        },
        userData: userData,
        ...additionalProductInfo,
      });
    },
    // TODO - Re-enable, requires dependency on a file we don't have yet.
    // dispatchAddToAutoOrderEvent(mpId: number, frequencyId: string, quantity: number, source: string) {
    //   const frequencyInfo = getAutoOrderTypeByFrequenceId(frequencyId);
    //   const frequencyText = "Every " + frequencyInfo.description;
    //   dispatchAnalyticsEvent({
    //     eventName: "autoOrderAdd",
    //     mpId: mpId,
    //     frequency: frequencyText,
    //     quantity: quantity,
    //     source: source,
    //   });
    // },
    /**
     * Provides a custom page view event when the tag needs additional data, like current user or other product info.
     * This was initially for use with a Pinterest tag, but could be added elsewhere.
     * @param {object} product optional, any product details if viewed on the PDP.
     */
    dispatchPageView({ product }: DispatchPageViewProps) {
      // NOTE: If you change anything here name wise, make sure you update the version in n32-ec-functions.js
      const eventName = "pageViewWithAdditionalInfo";
      const userData: AnalyticsUserData | undefined = convertUserStateToUserDataForAnalytics(user, totalOrders);
      const eCommerceInfo = !product
        ? undefined
        : {
            detail: {
              products: [
                {
                  id: product.mpId,
                  category: product.category,
                },
              ],
            },
          };
      dispatchAnalyticsEvent({
        eventName: eventName,
        userData: userData,
        ecommerce: eCommerceInfo,
      });
    },
    dispatchEmailSubscriptionEvent(email: string, phone?: string) {
      console.log("dispatchEmailSubscriptionEvent()");
      dispatchAnalyticsEvent({
        eventName: "emailSubscriptionSiteFooterSuccess",
        subscribedEmail: email,
      });
    },
    dispatchCheckoutStep({ step }: DispatchCheckoutViewProps) {
      const eventName = "checkoutStep";
      const products = !shoppingCart
        ? undefined
        : shoppingCart.vendorOrders.flatMap((vendorOrder) => {
            return vendorOrder.products.map((product) => {
              return {
                id: product.mpId,
                name: product.productTitle,
                quantity: product.quantity,
                price: product.unitPrice,
                subTotal: product.subtotal,
                category: product.categoryName,
              };
            });
          });
      const eCommerceInfo = {
        checkout: {
          actionField: {
            step: step,
          },
          products: products,
          shoppingCartSummary: getShoppingCartSummary(),
        },
      };
      const userData: AnalyticsUserData | undefined = convertUserStateToUserDataForAnalytics(user, totalOrders);
      dispatchAnalyticsEvent({
        eventName: eventName,
        ecommerce: eCommerceInfo,
        userData: userData,
      });
    },
    dispatchPurchase(checkoutResponse: CheckoutResponse) {
      const eventName = "purchase";
      const { firstPartyDetails } = checkoutResponse;
      const products = checkoutResponse?.vendorOrders?.flatMap((vendorOrder) => {
        return vendorOrder.products.map(
          ({
            mpId,
            productTitle,
            unitPrice,
            quantity,
            brandName,
            categoryName,
            subtotal,
            productLink,
            largeImagePath,
          }) => {
            return {
              item_id: String(mpId),
              item_name: productTitle,
              price: unitPrice,
              quantity: quantity,
              item_brand: brandName,
              item_category: categoryName,
              item_absolute_url: `${process.env.NEXT_PUBLIC_BASE_URL}${productLink}`,
              item_absolute_image: `${process.env.NEXT_PUBLIC_BASE_URL}${largeImagePath}`,
              item_subtotal: subtotal,
            };
          },
        );
      });
      const ecommerceInfo = {
        order_id: checkoutResponse?.coId,
        currency: "USD",
        value: checkoutResponse?.total,
        items: products,
        items_quantity: products?.length,
        subtotal_value: checkoutResponse?.subTotal,
        transaction_id: checkoutResponse?.coId,
        shipping: checkoutResponse?.standardShipping,
        tax: checkoutResponse?.totalTax,
        Email: firstPartyDetails?.email,
        Phone: firstPartyDetails?.phone,
        Name: firstPartyDetails?.name,
        Address: firstPartyDetails?.address,
        firstName: firstPartyDetails?.firstName,
        lastName: firstPartyDetails?.lastName,
        amplifiedPLTVScore: checkoutResponse?.amplifiedPLTVScore,
      };
      dispatchAnalyticsEvent({
        eventName,
        event_id: checkoutResponse.coId,
        ecommerce: ecommerceInfo,
      });
    },
    dispatchPlaceOrder({
      placeOrderConfirmationDetails,
      selectedShippingAddress,
    }: {
      placeOrderConfirmationDetails: CheckoutResponse;
      selectedShippingAddress: Account.Addresses.Address | undefined;
    }) {
      const eventName = "placeOrder";
      const totalShipping =
        (placeOrderConfirmationDetails?.standardShipping || 0.0) +
        (placeOrderConfirmationDetails?.heavyShipping || 0.0);
      const firstPurchase = user?.IsProfessionalOffice ? "Dental" : "Non Dental";
      const products = placeOrderConfirmationDetails?.vendorOrders?.flatMap((vendorOrder) => {
        return vendorOrder.products.map(
          ({
            mpId,
            manufacturerName,
            manufacturerId,
            brandId,
            unitPrice,
            quantity,
            brandName,
            categoryName,
            productTitle,
            authorizedDistributorFor,
          }) => {
            return {
              id: mpId,
              name: productTitle,
              price: unitPrice,
              category: categoryName,
              quantity,
              manufacturerId,
              manufacturerName,
              brandId,
              brandName: brandName,
              affiliation: vendorOrder.vendorId,
              authorizedDistributorFor,
            };
          },
        );
      });

      const ecommerceInfo = {
        purchase: {
          actionField: {
            id: String(placeOrderConfirmationDetails?.coId),
            revenue: placeOrderConfirmationDetails?.total,
            subTotal: placeOrderConfirmationDetails?.subTotal,
            shipping: totalShipping,
            gmv: placeOrderConfirmationDetails?.subTotal,
            numberOfOrders: placeOrderConfirmationDetails?.noOfOrders,
            firstOrder: placeOrderConfirmationDetails?.isFirstOrder,
            firstOrderDate: placeOrderConfirmationDetails?.firstOrderDate,
            lastOrderDate: moment().toISOString(),
          },
          userInfo: {
            shippingTo: {
              state: selectedShippingAddress?.RegionCD,
              city: selectedShippingAddress?.City,
              country: selectedShippingAddress?.CountryCD,
              zipCode: selectedShippingAddress?.PostalCD,
              street1: selectedShippingAddress?.Streets[0],
              street2: selectedShippingAddress?.Streets[1],
            },
          },
          products,
          purchaseType: placeOrderConfirmationDetails?.isFirstOrder && user?.IsProfessionalOffice ? "New" : "Repeat",
          ...(!placeOrderConfirmationDetails?.isFirstOrder && { firstPurchase }),
        },
      };
      dispatchAnalyticsEvent({
        eventName,
        ecommerce: ecommerceInfo,
      });
    },
  };
}

export function dispatchAddEvent({ event_id, checkoutCart, currency, eventName, ...rest }: AddInfoEventProps) {
  if (checkoutCart) {
    const items = convertShoppingCartProductToAnalyticsReportProductV2(checkoutCart);
    dispatchAnalyticsEvent({
      eventName,
      event_id,
      ecommerce: {
        currency,
        value: checkoutCart.subTotal,
        items,
        ...rest,
      },
    });
  }
}

export interface AddInfoEventProps {
  event_id: string | number;
  checkoutCart: ShoppingCartState | null;
  currency: string;
  eventName: string;
  [key: string]: any;
}

export interface DispatchAnalyticsEventProps {
  eventName: string;
  eventCallback?: () => void;
  eventTimeout?: number;
  userData?: AnalyticsUserData;
  shoppingCartSummary?: AnalyticsCartSummary;
  [keys: string]: any;
}

interface Impression {
  id: string | number;
  name: string;
  list: string;
  // list_position: number;
  brand?: string;
  category?: string;
  quantity?: number;
  price?: string | number;
  // variant: string;
  /** A Certona Slider schema */
  schema?: string;
}
export type ImpressionProduct = (
  | (Pick<Products.ManufacturerProduct, "MpName"> & {
      MpId: number;
    } & Partial<Products.ManufacturerProduct>)
  | (Products.Recommendations.ProductRecommendation & {
      BrandName?: string;
      MinQty?: number;
    })
  | undefined
) & {
  /** A Certona Slider schema */
  schema?: string;
};

export type AnalyticsReportProduct = {
  id: number;
  name?: string;
  quantity?: number;
  price?: number;
  brand?: string;
  category?: string;
  categories?: string[];
  variant?: string;
  coupon?: string;
  image_url?: string;
  product_detail_page_url?: string;
  retail_price?: string | number;
};

export type AnalyticsCartSummary = {
  totalQuantity: number;
  subTotal: number;
  totalShipping?: number;
  products: Array<AnalyticsSummaryCartProduct>;
};

export type AnalyticsSummaryCartProduct = {
  productId: number;
  name: string;
  quantity: number;
  price: number;
  subTotal?: number;
  category?: string;
};

export type AnalyticsUserData = {
  userId: number | string;
  userName?: string;
  userEmailAddress?: string;
  firstName?: string;
  lastName?: string;
  totalOrders?: number;
  isDentalCustomer?: boolean;
  hash?: string;
};

export type AnalyticsSource =
  | "Shopping Cart - Free Shipping Suggestion"
  | "Shopping Cart - Replenishment"
  | "Shopping Cart - Save For Later"
  | "Shopping Cart"
  | "Shopping Cart Acknowledgement"
  | "Product Detail Page"
  | "Product Detail Page - Vendor Rows"
  | "Product Detail Page - CTA"
  | "Product Detail - Reviews Page"
  | "Product Detail - Side Shopping Cart"
  | "Category Page"
  | "Search Page"
  | "Search Autocomplete"
  | "Weekly Specials"
  | "Top Sellers"
  | "New Arrivals"
  | "Dashboard"
  | "Easy Reorder"
  | "Vendor Detail"
  | "Product Reviews Page"
  | "Vendor Reviews Page"
  | "Shopping List"
  | "Order History Page"
  | "Order Detail Page"
  | "Certona"
  | "Buy Get Page"
  | "Unknown";

export type DispatchPageViewProps = {
  userState?: UserState;
  shoppingCartState?: ShoppingCartState;
  product?: {
    mpId: number;
    category?: string;
  };
};

export type DispatchCheckoutViewProps = {
  userState?: UserState;
  shoppingCartState?: ShoppingCartState;
  step: number;
};

type DispatchRegistrationEventProps = {
  isCheckout: boolean;
  userId: number;
  /** This is for Intercom user verification */
  userIdHash: string;
  emailAddress: string;
  firstName: string;
  lastName: string;
  isDentalOffice: boolean;
  subscribed: boolean;
  onBehalfOfBusiness?: boolean;
  businessType?: string;
  officeType?: string;
  whatBestDescribesYou?: string;
  officeSpecialty?: string;
  officeName?: string;
  officeContactFirstName?: string;
  officeContactLastName?: string;
  officeContactJobTitle?: string;
  dentalPracticeStructure?: string;
  dentalPracticeNumberOfLocations?: string;
  howYouHeardAboutUs?: string;
};

export type UserShippingAnalyticsInfo = {
  shippingState: string;
  shippingSubRegion: string;
};
